<template>
	<div
		v-show="show"
		class="chat-at-box"
		:style="{ 'left': pos.x + 'px', 'top': pos.y - 300 + 'px' }"
	>
		<div v-if="isMembersLoading && !showMembers.length" class="chat-at-box__status">
			正在加载群成员...
		</div>
		<div v-else-if="showMembers.length === 0" class="chat-at-box__status">
			{{ emptyText }}
		</div>
		<virtual-scroller
			v-else
			ref="scroller"
			class="chat-at-box__list"
			:items="showMembers"
			:item-height="40"
			:buffer="4"
			:show-reach-bottom-tip="false"
		>
			<template v-slot="{ item, index }">
				<div :key="memberKey(item)" class="chat-at-box__item" @mousedown.prevent @click="onSelectMember(item)">
					<group-member-item
						:group="group"
						:groupMembers="members"
						:member="item"
						:height="40"
						:active="activeIdx == index"
						:menu="false"
					></group-member-item>
				</div>
			</template>
		</virtual-scroller>
	</div>
</template>

<script>
import GroupMemberItem from "../group/GroupMemberItem.vue";
import VirtualScroller from "../common/VirtualScroller.vue";

const AT_MEMBER_PAGE_LIMIT = 500;

export default {
	name: "chatAtBox",
	components: {
		GroupMemberItem,
		VirtualScroller
	},
	props: {
		searchText: {
			type: String,
			default: ""
		},
		group: {
			type: Object
		},
		members: {
			type: Array
		}
	},
	data() {
		return {
			show: false,
			pos: {
				x: 0,
				y: 0
			},
			activeIdx: -1,
			visibleMembers: [],
			mentionRows: [],
			mentionSearchCache: new Map(),
			mentionSourceVersion: '',
			memberHydrationPromise: null
		};
	},
	computed: {
		normalizedSearchText() {
			return String(this.searchText || '').trim();
		},
		isMembersLoading() {
			return this.groupStore.isMembersLoading(this.group && this.group.id);
		},
		isMembersLoaded() {
			return this.groupStore.isMembersLoaded(this.group && this.group.id);
		},
		mentionMembersVersion() {
			const groupId = this.group && this.group.id ? this.group.id : '';
			const selfId = this.userStore.userInfo && this.userStore.userInfo.id ? this.userStore.userInfo.id : '';
			const memberLength = Array.isArray(this.members) ? this.members.length : 0;
			const memberCount = Number(this.group && this.group.memberCount) || 0;
			const loadedAt = Number(this.group && this.group.memberPage && this.group.memberPage.loadedAt) || 0;
			const ownerId = this.group && this.group.ownerId ? this.group.ownerId : '';
			return `${groupId}:${selfId}:${ownerId}:${memberLength}:${memberCount}:${loadedAt}`;
		},
		showMembers() {
			return this.visibleMembers;
		},
		emptyText() {
			if (this.isMembersLoading && !this.isMembersLoaded) {
				return '正在同步群成员...';
			}
			return this.normalizedSearchText ? '没有匹配的群成员' : '暂无可@的群成员';
		}
	},
	methods: {
		resolveMentionSearchText(member) {
			return String(member && member.showNickName || '');
		},
		resetMentionSearchState() {
			this.visibleMembers = [];
			this.mentionRows = [];
			this.mentionSearchCache = new Map();
			this.mentionSourceVersion = '';
			this.memberHydrationPromise = null;
		},
		ensureMentionRows() {
			if (this.mentionSourceVersion === this.mentionMembersVersion) {
				return;
			}
			const selfId = this.userStore.userInfo && this.userStore.userInfo.id;
			const mentionRows = [];
			(this.members || []).forEach((member) => {
				if (!member || member.userId == selfId || member.quit) {
					return;
				}
				mentionRows.push({
					member,
					searchText: this.resolveMentionSearchText(member)
				});
			});
			this.mentionRows = mentionRows;
			this.mentionSearchCache = new Map([['', mentionRows]]);
			this.mentionSourceVersion = this.mentionMembersVersion;
		},
		resolveCachedMentionRows(searchText) {
			this.ensureMentionRows();
			const keyword = String(searchText || '');
			const cachedRows = this.mentionSearchCache.get(keyword);
			if (cachedRows) {
				return cachedRows;
			}
			let baseRows = this.mentionRows;
			for (let idx = keyword.length - 1; idx >= 0; idx--) {
				const prefix = keyword.slice(0, idx);
				const prefixRows = this.mentionSearchCache.get(prefix);
				if (prefixRows) {
					baseRows = prefixRows;
					break;
				}
			}
			const nextRows = baseRows.filter((row) => row && row.searchText.startsWith(keyword));
			this.mentionSearchCache.set(keyword, nextRows);
			return nextRows;
		},
		refreshVisibleMembers() {
			if (!this.show) {
				this.visibleMembers = [];
				this.activeIdx = -1;
				return;
			}
			const searchText = this.normalizedSearchText;
			const nextMembers = [];
			const selfId = this.userStore.userInfo && this.userStore.userInfo.id;
			const allMembersText = '全体成员';
			if (this.group && this.group.ownerId == selfId && allMembersText.startsWith(searchText)) {
				nextMembers.push({
					userId: -1,
					showNickName: allMembersText
				});
			}
			this.resolveCachedMentionRows(searchText).forEach((row) => {
				if (row && row.member) {
					nextMembers.push(row.member);
				}
			});
			this.visibleMembers = nextMembers;
			if (!nextMembers.length) {
				this.activeIdx = -1;
				return;
			}
			if (this.activeIdx < 0) {
				this.activeIdx = 0;
				return;
			}
			if (this.activeIdx >= nextMembers.length) {
				this.activeIdx = nextMembers.length - 1;
			}
		},
		memberKey(member) {
			if (!member) {
				return 'unknown';
			}
			return member.userId === -1 ? 'all-members' : `member-${member.userId}`;
		},
		getOutsidePointerEventName() {
			return typeof window !== 'undefined' && 'PointerEvent' in window ? 'pointerdown' : 'mousedown';
		},
		addOutsidePointerListener() {
			if (typeof document === 'undefined') {
				return;
			}
			this.removeOutsidePointerListener();
			document.addEventListener(this.getOutsidePointerEventName(), this.onDocumentPointerDown, true);
		},
		removeOutsidePointerListener() {
			if (typeof document === 'undefined') {
				return;
			}
			document.removeEventListener(this.getOutsidePointerEventName(), this.onDocumentPointerDown, true);
		},
		onDocumentPointerDown(event) {
			if (!this.show) {
				return;
			}
			if (this.$el && this.$el.contains(event.target)) {
				return;
			}
			this.close();
		},
		open(pos) {
			this.show = true;
			this.pos = pos;
			this.refreshVisibleMembers();
			this.ensureMembersReady();
			this.$nextTick(() => {
				this.addOutsidePointerListener();
				this.scrollToIndex(0);
			});
		},
		close() {
			this.show = false;
			this.removeOutsidePointerListener();
			this.resetMentionSearchState();
		},
		moveUp() {
			if (this.activeIdx > 0) {
				this.activeIdx--;
				this.scrollToActive()
			}
		},
		moveDown() {
			if (this.activeIdx < this.showMembers.length - 1) {
				this.activeIdx++;
				this.scrollToActive()
			}
		},
		select() {
			if (this.activeIdx >= 0) {
				this.onSelectMember(this.showMembers[this.activeIdx])
			}
			this.close();
		},
		scrollToIndex(index) {
			const scroller = this.$refs.scroller;
			if (!scroller || typeof scroller.scrollToIndex !== 'function' || index < 0) {
				return;
			}
			scroller.scrollToIndex(index, {
				align: 'center'
			});
		},
		scrollToActive() {
			this.$nextTick(() => {
				this.scrollToIndex(this.activeIdx);
			});
		},
		ensureMembersReady() {
			const groupId = this.group && this.group.id;
			if (!groupId) {
				return Promise.resolve([]);
			}
			if (this.memberHydrationPromise) {
				return this.memberHydrationPromise;
			}
			const task = this.groupStore.refreshMember(groupId, {
				refreshOnline: false,
				pageLimit: AT_MEMBER_PAGE_LIMIT
			}).then(() => {
				if (this.groupStore.hasCompleteMemberSnapshot(groupId)) {
					const group = this.groupStore.findGroup(groupId);
					return (group && group.members) || [];
				}
				return this.groupStore.ensureGroupMembersFullyLoaded(groupId, {
					pageLimit: AT_MEMBER_PAGE_LIMIT,
					refreshOnline: false
				});
			}).catch(() => []).finally(() => {
				if (this.memberHydrationPromise === task) {
					this.memberHydrationPromise = null;
				}
			});
			this.memberHydrationPromise = task;
			return task;
		},
		onSelectMember(member) {
			this.$emit("select", member);
			this.close();
		}
	},
	watch: {
		searchText: {
			handler() {
				if (!this.show) {
					return;
				}
				this.activeIdx = 0;
				this.refreshVisibleMembers();
				this.scrollToActive();
			}
		},
		mentionMembersVersion: {
			handler() {
				if (!this.show) {
					return;
				}
				this.refreshVisibleMembers();
			}
		},
		showMembers: {
			handler(newMembers) {
				if (!this.show) {
					return;
				}
				if (!newMembers.length) {
					this.activeIdx = -1;
					return;
				}
				if (this.activeIdx < 0) {
					this.activeIdx = 0;
					return;
				}
				if (this.activeIdx >= newMembers.length) {
					this.activeIdx = newMembers.length - 1;
				}
			}
		}
	},
	beforeDestroy() {
		this.removeOutsidePointerListener();
	}
}
</script>

<style scoped lang="scss">
.chat-at-box {
	position: fixed;
	width: 200px;
	height: 300px;
	background-color: var(--im-background);
	box-shadow: var(--im-box-shadow);
	border-radius: 6px;
	overflow: hidden;

	&__list {
		height: 100%;
	}

	&__item {
		min-height: 40px;
	}

	&__status {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		padding: 0 16px;
		font-size: 13px;
		color: var(--im-text-color-light);
		text-align: center;
	}
}
</style>
